import { LitElement, css, html } from 'lit'

import { formatTermDate } from '../../utils/format-date.js'
import { formatPriceAmount } from '../../utils/format-price.js'

export class CtProgramCard extends LitElement {
  static properties = {
    program: { attribute: false },
  }

  constructor() {
    super()
    this.program = null
  }

  static styles = css`
    :host {
      display: block;
      min-width: 0;
      height: 100%;
      color: #33404c;
      font-family: Poppins, Arial, sans-serif;
    }

    * { box-sizing: border-box; }

    .card {
      display: flex;
      height: 100%;
      min-height: 352px;
      flex-direction: column;
      overflow: hidden;
      border: 1px solid #e1e8e8;
      border-radius: 16px;
      background: #fff;
      box-shadow: 0 2px 6px rgba(0, 0, 0, .06);
      color: inherit;
      cursor: pointer;
      text-decoration: none;
      transition: border-color 220ms ease, box-shadow 220ms ease;
    }

    .card:hover {
      border-color: #cfd8d8;
      box-shadow: 0 3px 9px rgba(0, 0, 0, .16);
    }

    .card,
    .card * { cursor: pointer; }

    .card:focus-visible {
      outline: 3px solid rgba(247,147,30,.35);
      outline-offset: 3px;
    }

    .visual {
      position: relative;
      height: 220px;
      overflow: hidden;
      background: #38636a;
    }

    .visual::after {
      position: absolute;
      z-index: 0;
      inset: 40% 0 0;
      background: linear-gradient(transparent, rgba(0, 0, 0, 0.9));
      content: '';
    }

    .image {
      width: 100%;
      height: 100%;
      display: block;
      object-fit: cover;
      transform: scale(var(--ct-program-image-scale, 1.08));
    }

    .image-hover-overlay {
      position: absolute;
      z-index: 1;
      inset: 0;
      background: rgba(255, 255, 255, .1);
      opacity: 0;
      pointer-events: none;
      transition: opacity 180ms ease;
    }

    .card:hover .image-hover-overlay { opacity: 1; }

    .badge,
    .duration {
      position: absolute;
      z-index: 1;
      top: 12px;
      padding: 6px 10px;
      border-radius: 16px;
      font-size: 14px;
      font-weight: 600;
      line-height: 19px;
    }

    .badge {
      left: 12px;
      background: #f7eadc;
      color: #80501d;
    }

    .badge--presale {
      background: #dcefd2;
      color: #35613d;
    }

    .badge--new {
      background: #d4efed;
      color: #176762;
    }

    .duration {
      display: inline-flex;
      right: 12px;
      align-items: center;
      gap: 6px;
      padding-right: 14px;
      background: rgba(255, 255, 255, .92);
      color: #33404c;
      backdrop-filter: blur(8px);
    }

    .duration svg {
      width: 18px;
      height: 18px;
      display: block;
      fill: none;
      stroke: currentColor;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 1.8;
    }

    .visual-copy {
      position: absolute;
      z-index: 1;
      right: 16px;
      bottom: 14px;
      left: 16px;
    }

    .destination {
      display: block;
      margin-bottom: 2px;
      color: #f7a13a;
      font-size: 18px;
      font-weight: 600;
      line-height: 18px;
      letter-spacing: .02em;
      text-transform: uppercase;
    }

    .program-title {
      margin: 0;
      color: #fff;
      font-family: Poppins, Arial, sans-serif;
      font-size: 22px;
      font-weight: 400;
      line-height: 28px;
    }

    .content {
      display: flex;
      flex: 1;
      flex-direction: column;
      padding: 12px 16px 16px;
    }

    .schedule-overview {
      display: grid;
      grid-template-columns: .8fr 1.2fr;
      align-items: center;
      gap: 16px;
      margin: 0 0 12px;
      padding: 2px 0 12px;
      border-bottom: 1px solid #dfe7e7;
    }

    .schedule-count {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-left: 2px;
    }

    .schedule-count strong {
      color: #18586a;
      font-size: 32px;
      font-weight: 500;
      line-height: 30px;
      transform: translateY(1px);
    }

    .schedule-count span,
    .schedule-next span {
      display: block;
      color: #7d898d;
      font-size: 11px;
      font-weight: 600;
      line-height: 15px;
      letter-spacing: .07em;
      text-transform: uppercase;
    }

    .schedule-next {
      position: relative;
      min-width: 0;
      padding-left: 14px;
    }

    .schedule-next::before {
      position: absolute;
      top: 2px;
      bottom: 2px;
      left: 0;
      width: 1px;
      background: #dfe7e7;
      content: '';
    }

    .schedule-next strong {
      display: block;
      margin-top: 3px;
      color: #35484e;
      font-size: 16px;
      font-weight: 600;
      line-height: 20px;
    }

    .footer {
      display: grid;
      grid-template-columns: minmax(0, .8fr) minmax(0, 1.2fr);
      align-items: center;
      gap: 16px;
    }

    .price-block {
      display: flex;
      min-height: 44px;
      flex-direction: column;
      justify-content: center;
      margin-left: 4px;
    }

    .price-label {
      display: block;
      color: #849094;
      font-size: 14px;
      line-height: 14px;
    }

    .price {
      display: block;
      color: #2f8a4c;
      font-size: 20px;
      font-weight: 600;
      line-height: 26px;
      white-space: nowrap;
    }

    .cta {
      display: inline-flex;
      min-height: 44px;
      align-items: center;
      justify-content: center;
      padding: 0 18px;
      border-radius: 12px;
      background: #f7931e;
      color: #fff;
      font-size: 15px;
      font-weight: 500;
      letter-spacing: .04em;
      line-height: 1;
      text-decoration: none;
      text-transform: uppercase;
      white-space: nowrap;
      width: 100%;
      min-width: 0;
    }

    .cta { transition: background 160ms ease, transform 160ms ease; }

    .cta:hover {
      background: #ff9f2e;
      transform: translateY(-1px);
    }

    @media (min-width: 1200px) {
      .card { min-height: 344px; }
      .visual { height: 212px; }
      .visual-copy { bottom: 10px; }
    }

    @media (min-width: 768px) and (max-width: 1199px) {
      .program-title { font-size: 21px; line-height: 27px; }
    }

    @media (max-width: 767px) {
      .visual-copy { bottom: 10px; }
      .destination { margin-bottom: 0; font-size: 16px; line-height: 19px; }
      .program-title { font-size: 21px; line-height: 26px; }
      .schedule-count span,
      .schedule-next span {
        font-size: clamp(10px, calc(6px + 1.25vw), 11px);
        line-height: clamp(14px, calc(10px + 1.25vw), 15px);
        letter-spacing: .04em;
      }
      .card { min-height: 0; border-radius: 16px; }
      .visual { height: clamp(208px, calc(203.2px + 1.4vw), 214px); }
      .content {
        padding:
          clamp(10px, calc(6.8px + .94vw), 14px)
          clamp(12px, calc(8.8px + .94vw), 16px)
          clamp(11px, calc(7px + 1.18vw), 16px);
      }
      .schedule-overview {
        grid-template-columns: minmax(0, .8fr) minmax(0, 1.2fr);
        gap: clamp(12px, 2vw, 16px);
        margin-bottom: clamp(10px, calc(6.8px + .94vw), 14px);
        padding-top: 0;
        padding-bottom: clamp(8px, calc(4.8px + .94vw), 12px);
      }
      .schedule-count { gap: clamp(6px, calc(-2px + 2.5vw), 8px); }
      .schedule-count strong {
        font-size: clamp(28px, calc(16px + 3.75vw), 32px);
        line-height: clamp(27px, calc(18px + 2.8vw), 30px);
      }
      .schedule-next { padding-left: clamp(12px, 2vw, 14px); }
      .schedule-next strong {
        margin-top: 1px;
        font-size: clamp(13px, calc(1px + 3.75vw), 16px);
        line-height: clamp(18px, calc(10px + 2.5vw), 20px);
        letter-spacing: -.01em;
        white-space: nowrap;
      }
      .footer { align-items: center; gap: clamp(12px, 2vw, 16px); }
      .price-block,
      .cta { min-height: clamp(40px, calc(36.8px + .94vw), 44px); }
      .price {
        font-size: clamp(17px, calc(5px + 3.75vw), 20px);
        line-height: clamp(23px, calc(11px + 3.75vw), 26px);
      }
      .cta {
        padding-right: clamp(10px, 2.35vw, 18px);
        padding-left: clamp(10px, 2.35vw, 18px);
        font-size: clamp(13px, calc(5px + 2.5vw), 15px);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .card, .cta, .image-hover-overlay { transition: none; }
      .cta:hover { transform: none; }
    }
  `

  render() {
    if (!this.program) return ''

    const program = this.program
    const cardId = program.pageUrl?.startsWith('#')
      ? program.pageUrl.slice(1)
      : `catalog-program-${program.programKey}`
    const nearest = program.terms[0]
    const date = formatTermDate(
      nearest.startDate,
      nearest.endDate
    )
    const termCount = program.terms.length
    const lastTwoDigits = termCount % 100
    const lastDigit = termCount % 10
    const fewTerms = lastDigit >= 2 && lastDigit <= 4 &&
      (lastTwoDigits < 12 || lastTwoDigits > 14)
    const availabilityWord = termCount === 1
      ? 'Dostępny'
      : fewTerms ? 'Dostępne' : 'Dostępnych'
    const termWord = termCount === 1
      ? 'termin'
      : fewTerms ? 'terminy' : 'terminów'

    return html`
      <a class="card" id=${cardId} href=${program.pageUrl}>
        <div class="visual">
          <img
            class="image"
            src=${program.image}
            alt=${program.imageAlt || `${program.destination} – ${program.title}`}
            @error=${event => {
              if (
                program.fallbackImage &&
                event.currentTarget.src !== program.fallbackImage
              ) {
                event.currentTarget.src = program.fallbackImage
              }
            }}
            style=${`
              object-position: ${program.imagePosition || 'center center'};
              transform-origin: ${program.imagePosition || 'center center'};
              --ct-program-image-scale: ${program.imageScale || 1.08};
            `}
          >
          <span class="image-hover-overlay" aria-hidden="true"></span>
          ${program.label
            ? html`
                <span class="badge badge--${program.labelVariant || 'default'}">
                  ${program.label}
                </span>
              `
            : ''}
          <span class="duration">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="9"></circle>
              <path d="M12 7v5l3 2"></path>
            </svg>
            ${program.duration}
          </span>
          <div class="visual-copy">
            <span class="destination">${program.destination}</span>
            <h3 class="program-title">${program.title}</h3>
          </div>
        </div>
        <div class="content">
          <div class="schedule-overview">
            <div class="schedule-count">
              <strong>${termCount}</strong>
              <span>${availabilityWord}<br>${termWord}</span>
            </div>
            <div class="schedule-next">
              <span>Najbliższy wyjazd</span>
              <strong>${date}</strong>
            </div>
          </div>
          <div class="footer">
            <div class="price-block">
              <span class="price">${program.pricesDiffer ? 'Od ' : ''}${formatPriceAmount(program.priceFrom)} zł</span>
              <span class="price-label">za osobę</span>
            </div>
            <span class="cta">Zobacz program</span>
          </div>
        </div>
      </a>
    `
  }
}

if (!customElements.get('ct-program-card')) {
  customElements.define('ct-program-card', CtProgramCard)
}
